import classNames from 'classnames';
import { Icon } from 'lego-on-react';
import * as React from 'react';

import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { Shape2, Shape2Icon } from '../../components/Shape2/Shape2';
import { TextInputDecorator } from '../../components/TextInputDecorator/TextInputDecorator';
import { Bem } from '../../service/Bem/Bem';
import { KeyCode } from '../../typings/common';

import './CanvasEditableLabel.css';

export interface CanvasEditableLabelProps {
    cls?: string;
    editBtnCls?: string;
    editInputCls?: string;
    value: string;
    isEditMode: boolean;
    onOpenEditor: (name: string) => void;
    onChange: (name: string) => void;
    onSave: (name: string) => void;
    onCancel: () => void;
    hasClear?: boolean;
    enableEmptyInput?: boolean;
    withControls?: boolean;
    iconRight?: React.ReactElement<{}>;
}

const blockClass = 'canvas-editable-label';

export class CanvasEditableLabel extends React.PureComponent<CanvasEditableLabelProps> {
    static defaultProps = {
        hasClear: true,
        enableEmptyInput: true,
        withControls: true
    };

    private static checkIcon = <Icon size={'s'} glyph={'type-check'} cls={Bem.cls(blockClass, 'btn-icon')} />;

    private static crossIcon = <Icon size={'s'} glyph={'type-cross'} cls={Bem.cls(blockClass, 'btn-icon')} />;

    render() {
        const { isEditMode, cls } = this.props;

        return (
            <div
                onClick={this.onEditClick}
                className={classNames(
                    Bem.cls({
                        block: blockClass,
                        mods: { mode: isEditMode ? 'edit' : 'default ' }
                    }),
                    cls
                )}
            >
                {isEditMode ? this.getEditModeContent() : this.getDefaultModeContent()}
            </div>
        );
    }

    private getDefaultModeContent = () => {
        const { value, editBtnCls = '' } = this.props;

        return (
            <>
                <div className={Bem.cls(blockClass, 'name-label')}>{value}</div>
                <ButtonDecorator
                    cls={classNames(Bem.cls(blockClass, 'btn', { action: 'edit-name' }), editBtnCls)}
                    theme={'clear'}
                    size={'s'}
                >
                    <Shape2 width={12} height={12} icon={Shape2Icon.edit} />
                </ButtonDecorator>
            </>
        );
    };

    private onSave = () => {
        const { value, onSave } = this.props;

        onSave(value);
    };

    private getEditModeContent = () => {
        const { value, hasClear, onCancel, onChange, editInputCls, withControls, iconRight } = this.props;

        return (
            <>
                <TextInputDecorator
                    hasClear={hasClear}
                    cls={classNames(Bem.cls(blockClass, 'input'), editInputCls)}
                    onChange={onChange}
                    size={'s'}
                    theme={'normal'}
                    text={value}
                    autoFocus={true}
                    onKeyDown={this.onKeyDown}
                    onBlur={withControls ? undefined : this.onBlur}
                    iconRight={iconRight}
                />
                {withControls && (
                    <>
                        <ButtonDecorator
                            cls={Bem.cls(blockClass, 'btn', { action: 'save' })}
                            theme={'pseudo'}
                            size={'s'}
                            onClick={this.onSave}
                            icon={CanvasEditableLabel.checkIcon}
                        />
                        <ButtonDecorator
                            cls={Bem.cls(blockClass, 'btn', {
                                action: 'cancel'
                            })}
                            theme={'pseudo'}
                            size={'s'}
                            onClick={onCancel}
                            icon={CanvasEditableLabel.crossIcon}
                        />
                    </>
                )}
            </>
        );
    };

    private onEditClick = () => {
        const { value, onOpenEditor, isEditMode } = this.props;

        if (isEditMode) {
            return;
        }

        onOpenEditor(value);
    };

    private onBlur = () => {
        const { withControls, enableEmptyInput, value, onCancel } = this.props;

        if (!withControls) {
            if (!enableEmptyInput && (!value || value.trim() === '')) {
                onCancel();
                return;
            }

            this.onSave();
        }
    };

    private onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
        e.stopPropagation();
        const code = e.keyCode;

        if (code === KeyCode.ESC) {
            this.props.onCancel();
        } else if (code === KeyCode.RETURN) {
            this.onBlur();
        }
    };
}
